import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useTimezone } from '@uplift/contexts';

import { CalendarDateService, DateObject } from '@uplift/services';
import { useAllTimezones } from '@uplift/queries';
import { DateFormats } from '@uplift/constants';
import { Box, Typography } from '@mui/material';

export type TableCellDateTimeProps = {
  className?: string;
  value?: DateObject | string;
};


const useStyles = makeStyles()(theme => ({
  root: {
    lineHeight: '1.2em',
    '& > span': {
      display: 'block'
    }
  },
  time: {
    color: theme.palette.grey[500]
  }
}));


export const TableCellDateTime: React.VFC<TableCellDateTimeProps> = ({ value: _value, className }) => {
  const value = React.useMemo(() => CalendarDateService.date(_value), [ _value ]);
  const timezone = useTimezone();
  const timezones = useAllTimezones();
  const tz = timezones.find(t => t.ianaName === timezone)?.shortName;
  const { classes, cx } = useStyles();

  if (CalendarDateService.isDateObject(value)) {
    const tzValue = value.tz(timezone);

    return (
      <span className={cx([ classes.root, className ])}>
        <span>{tzValue.format('MM/DD/YYYY')}</span>
        <span className={classes.time}>{tzValue.format('h:mm A')} {tz}</span>
      </span>
    );
  }

  return null;
};


export const TableCellDate: React.VFC<TableCellDateTimeProps> = ({ value: _value, className }) => {
  const value = React.useMemo(() => CalendarDateService.date(_value), [ _value ]);
  const timezone = useTimezone();
  const { classes, cx } = useStyles();

  if (CalendarDateService.isDateObject(value)) {
    const tzValue = value.tz(timezone);

    return (
      <span className={cx([ classes.root, className ])}>
        {tzValue.format('MM/DD/YYYY')}
      </span>
    );
  }

  return null;
};


export const TableCellTime: React.VFC<TableCellDateTimeProps> = ({ value: _value, className }) => {
  const value = React.useMemo(() => CalendarDateService.date(_value), [ _value ]);
  const timezone = useTimezone();
  const timezones = useAllTimezones();
  const tz = timezones.find(t => t.ianaName === timezone)?.shortName;
  const { classes, cx } = useStyles();

  if (CalendarDateService.isDateObject(value)) {
    const tzValue = value.tz(timezone);

    return (
      <span className={cx([ classes.root, className ])}>
        {tzValue.format('h:mm A')} {tz}
      </span>
    );
  }

  return null;
};


export const TableCellDateOrDateTime: React.VFC<TableCellDateTimeProps> = ({ value }) => {
  if (CalendarDateService.date(value, DateFormats.ISO_DATE, true).isValid()) {
    return <TableCellDate value={value} />;
  }

  return <TableCellDateTime value={value} />;
};


export const TableCellDateTimeWithDuration: React.VFC<TableCellDateTimeProps> = ({ value }) => {
  const timezone = useTimezone();

  if (!value) {
    return null;
  }

  const dateObject = CalendarDateService.date(value);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
      <Typography variant="body2" color="textPrimary">{dateObject.tz(timezone).format('MMM D, YYYY h:mm A z')}</Typography>
      <Typography component="span" variant="body2">{dateObject.fromNow()}</Typography>
    </Box>
  );
};
